import type { VisualFinding } from '../../types/api';

/** Badge wording per severity. Both are notes; neither is a gate. */
const SEVERITY_LABEL: Readonly<Record<VisualFinding['severity'], string>> = {
  warn: 'Warning',
  fail: 'Failed check',
};

/**
 * Plain wording for the guardrail's rule names. Deliberately not exhaustive
 * over a union: the rule vocabulary is the backend's to grow, and a rule this
 * build has not heard of has to render as itself rather than disappear.
 */
const RULE_LABEL: Readonly<Record<string, string>> = {
  contrast: 'Text readability',
  headline_length: 'Headline length',
  body_length: 'Body length',
  logo_missing: 'Logo missing',
  image_slot_empty: 'Empty image slot',
  logo_over_text: 'Logo over text',
  backdrop_missing: 'Backdrop missing',
  icon_over_text: 'Icon over text',
};

interface VisualFindingsPanelProps {
  findings: VisualFinding[];
  /**
   * Which slide these belong to, for the panel's own label. Null when the run
   * rendered a single picture — there is nothing to number it against.
   */
  slideNumber: number | null;
}

/**
 * Deterministic visual-guardrail output for the slide on screen (SRS §3 stage 6):
 * contrast, legibility, logo placement, and whether a layout with a picture slot
 * actually has a picture in it.
 *
 * Presented like the fact-check panel and for the same reason — these are
 * advisory at review time. The guardrail already ran server-side before the
 * slides reached this screen, so nothing here disables approve, reject or the
 * layer editor. The user keeps the decision.
 */
export function VisualFindingsPanel({ findings, slideNumber }: VisualFindingsPanelProps) {
  return (
    <div className="visual-findings">
      <p className="visual-findings__lede">
        {slideNumber === null ? 'Design check' : `Design check — slide ${slideNumber}`}
      </p>

      {findings.length === 0 ? (
        <p className="visual-findings__empty">
          Nothing flagged. Text is readable, the logo is clear of the edges, all text fits and every
          picture slot is filled.
        </p>
      ) : (
        <>
          <ul className="finding-list">
            {findings.map((finding, index) => (
              <li
                className={`finding finding--${finding.severity}`}
                key={`${finding.rule}-${index}`}
              >
                <div className="finding__head">
                  <span className="finding__badge">{SEVERITY_LABEL[finding.severity]}</span>
                  <span className="finding__rule">{RULE_LABEL[finding.rule] ?? finding.rule}</span>
                </div>
                <p className="finding__message">{finding.message}</p>
              </li>
            ))}
          </ul>
          <p className="visual-findings__note">
            Advice only — none of this blocks you. Approve it, reject it, or fix it in the layer
            editor.
          </p>
        </>
      )}
    </div>
  );
}
